import { sendGAEvent as rawSendGAEvent } from "@next/third-parties/google";
import { batchedSendGAEvent } from "./analytics-batching";

// Global gtag types
declare global {
  interface Window {
    gtag: (
      command: "config" | "event" | "js" | "set",
      targetId: string,
      config?: any
    ) => void;
  }
}

type GACommand = "event" | "config" | "set";

const sendGAEvent = (
  command: GACommand,
  eventName: string,
  parameters?: Record<string, unknown>
) => {
  try {
    if (parameters) {
      rawSendGAEvent(command, eventName, parameters);
      return;
    }

    rawSendGAEvent(command, eventName);
  } catch {
    // Keep analytics best-effort so product flows never fail on tracking.
  }
};

/**
 * Analytics tracking utilities for consistent event tracking across the application
 */

// Configuration for which events should be batched vs sent immediately
const CRITICAL_EVENTS = new Set([
  "sign_up",
  "login",
  "onboarding_complete",
  "api_key_generate",
]);

// Helper function to determine if event should be batched
const shouldBatchEvent = (eventName: string): boolean => {
  return !CRITICAL_EVENTS.has(eventName);
};

// Development console logging for GA events
const logGAEventForDev = (
  eventName: string,
  parameters: any,
  isBatched: boolean = false
) => {
  if (process.env.NODE_ENV === "development") {
    const batchStatus = isBatched ? "🔄 [BATCHED]" : "⚡ [IMMEDIATE]";
    console.log(`🔍 GA Event ${batchStatus}:`, {
      event: eventName,
      parameters,
      timestamp: new Date().toISOString(),
      batched: isBatched,
    });
  }
};

// Smart event sender that uses batching for non-critical events
const smartSendGAEvent = (eventName: string, parameters: any) => {
  try {
    const isBatched = shouldBatchEvent(eventName);

    // Log for development
    logGAEventForDev(eventName, parameters, isBatched);

    if (isBatched) {
      batchedSendGAEvent(eventName, parameters);
      return;
    }
    sendGAEvent("event", eventName, parameters);
  } catch {
    // Keep analytics best-effort so product flows never fail on tracking.
  }
};

// User Authentication Events
export const trackUserRegistration = (
  method: string = "email",
  email?: string
) => {
  // Set up user tracking for new registration
  if (email) {
    const anonymousUserId = generateAnonymousUserId(email);
    setUserId(anonymousUserId);

    // Set user properties for new user
    setUserProperties({
      userType: "new",
      registrationDate: new Date().toISOString(),
      onboardingCompleted: false,
      userSegment: "new_user",
    });
  }

  const eventData = {
    method,
    event_category: "authentication",
    event_label: "user_registration_completed",
    custom_parameters: {
      timestamp: new Date().toISOString(),
      registration_method: method,
      has_user_id: !!email,
    },
  };

  // Log for development
  logGAEventForDev("sign_up", eventData, false);

  sendGAEvent("event", "sign_up", eventData);
};

export const trackUserLogin = (method: string = "email", email?: string) => {
  // Set up user tracking for returning user
  if (email) {
    const anonymousUserId = generateAnonymousUserId(email);
    setUserId(anonymousUserId);

    // Set user properties for returning user
    setUserProperties({
      userType: "returning",
      userSegment: "returning_user",
    });
  }

  const userId = getUserId();
  const eventData = {
    method,
    event_category: "authentication",
    event_label: "user_login_success",
    custom_parameters: {
      timestamp: new Date().toISOString(),
      login_method: method,
      has_user_id: !!userId,
    },
  };

  // Log for development
  logGAEventForDev("login", eventData, false);

  sendGAEvent("event", "login", eventData);
};

// Onboarding Events
export const trackOnboardingStart = () => {
  sendGAEvent("event", "onboarding_start", {
    event_category: "user_journey",
    event_label: "onboarding_flow_started",
    custom_parameters: {
      timestamp: new Date().toISOString(),
      step: "initial",
    },
  });
};

export const trackOnboardingStep = (stepName: string, stepNumber: number) => {
  sendGAEvent("event", "onboarding_step_completed", {
    event_category: "user_journey",
    event_label: `onboarding_${stepName}_completed`,
    custom_parameters: {
      step_name: stepName,
      step_number: stepNumber,
      timestamp: new Date().toISOString(),
    },
  });
};

export const trackOnboardingComplete = () => {
  // Update user properties when onboarding is completed
  setUserProperties({
    onboardingCompleted: true,
    userSegment: "onboarded_user",
  });

  const userId = getUserId();
  const eventData = {
    event_category: "user_journey",
    event_label: "onboarding_flow_completed",
    custom_parameters: {
      timestamp: new Date().toISOString(),
      completion_status: "success",
      has_user_id: !!userId,
    },
  };

  // Log for development
  logGAEventForDev("onboarding_complete", eventData, false);

  sendGAEvent("event", "onboarding_complete", eventData);
};

// GitHub Integration Events
export const trackGitHubConnect = () => {
  sendGAEvent("event", "github_connect", {
    event_category: "integration",
    event_label: "github_account_connected",
    custom_parameters: {
      integration_type: "github",
      timestamp: new Date().toISOString(),
    },
  });
};

export const trackGitHubDisconnect = () => {
  sendGAEvent("event", "github_disconnect", {
    event_category: "integration",
    event_label: "github_account_disconnected",
    custom_parameters: {
      integration_type: "github",
      timestamp: new Date().toISOString(),
    },
  });
};

// Dashboard Events
export const trackAPIKeyCreation = () => {
  sendGAEvent("event", "api_key_create", {
    event_category: "dashboard_action",
    event_label: "api_key_created",
    custom_parameters: {
      action_type: "create",
      resource_type: "api_key",
      timestamp: new Date().toISOString(),
    },
  });
};

export const trackProjectView = (projectId: string) => {
  sendGAEvent("event", "project_view", {
    event_category: "dashboard_navigation",
    event_label: "project_sandbox_viewed",
    custom_parameters: {
      project_id: projectId,
      view_type: "sandbox",
      timestamp: new Date().toISOString(),
    },
  });
};

export const trackLogsCleared = (logType: string = "general") => {
  sendGAEvent("event", "logs_cleared", {
    event_category: "dashboard_action",
    event_label: "logs_cleared_by_user",
    custom_parameters: {
      log_type: logType,
      action_type: "clear",
      timestamp: new Date().toISOString(),
    },
  });
};

// Navigation Events
export const trackImportantNavigation = (
  fromPage: string,
  toPage: string,
  trigger: string
) => {
  sendGAEvent("event", "important_navigation", {
    event_category: "navigation",
    event_label: `navigation_${fromPage}_to_${toPage}`,
    custom_parameters: {
      from_page: fromPage,
      to_page: toPage,
      trigger_type: trigger, // 'button', 'link', 'redirect', etc.
      timestamp: new Date().toISOString(),
    },
  });
};

// Terms and Legal Events
export const trackTermsAcceptance = (source: string = "registration") => {
  sendGAEvent("event", "terms_accepted", {
    event_category: "legal_compliance",
    event_label: "terms_of_service_accepted",
    custom_parameters: {
      acceptance_source: source,
      timestamp: new Date().toISOString(),
    },
  });
};

// Error Tracking
export const trackError = (
  errorType: string,
  errorMessage: string,
  location: string
) => {
  sendGAEvent("event", "error_occurred", {
    event_category: "error_tracking",
    event_label: `error_${errorType}`,
    custom_parameters: {
      error_type: errorType,
      error_message: errorMessage,
      error_location: location,
      timestamp: new Date().toISOString(),
    },
  });
};

// Quickstart Page Events
export const trackQuickstartCodeCopy = (codeType: string, step: string) => {
  smartSendGAEvent("code_copy", {
    event_category: "quickstart_engagement",
    event_label: `code_copied_${codeType}`,
    custom_parameters: {
      code_type: codeType,
      step: step,
      timestamp: new Date().toISOString(),
    },
  });
};

export const trackAPIKeyGeneration = () => {
  const eventData = {
    event_category: "quickstart_engagement",
    event_label: "api_key_generated_quickstart",
    custom_parameters: {
      generation_location: "quickstart_page",
      timestamp: new Date().toISOString(),
    },
  };

  // Log for development
  logGAEventForDev("api_key_generate", eventData, false);

  sendGAEvent("event", "api_key_generate", eventData);
};

export const trackTemplateView = (templateName: string) => {
  sendGAEvent("event", "template_view", {
    event_category: "quickstart_engagement",
    event_label: `template_viewed_${templateName}`,
    custom_parameters: {
      template_name: templateName,
      interaction_type: "modal_open",
      timestamp: new Date().toISOString(),
    },
  });
};

export const trackTemplateCodeCopy = (templateName: string) => {
  sendGAEvent("event", "template_code_copy", {
    event_category: "quickstart_engagement",
    event_label: `template_code_copied_${templateName}`,
    custom_parameters: {
      template_name: templateName,
      action_type: "code_copy",
      timestamp: new Date().toISOString(),
    },
  });
};

// Content Engagement Events
export const trackPageScrollDepth = (page: string, scrollDepth: number) => {
  smartSendGAEvent("scroll_depth", {
    event_category: "content_engagement",
    event_label: `scroll_${scrollDepth}%_${page}`,
    custom_parameters: {
      page_name: page,
      scroll_percentage: scrollDepth,
      timestamp: new Date().toISOString(),
    },
  });
};

export const trackContentReadTime = (page: string, timeSpent: number) => {
  smartSendGAEvent("content_read_time", {
    event_category: "content_engagement",
    event_label: `read_time_${page}`,
    custom_parameters: {
      page_name: page,
      time_spent_seconds: timeSpent,
      timestamp: new Date().toISOString(),
    },
  });
};

// HelpScout Beacon Events
export const trackHelpScoutOpen = () => {
  sendGAEvent("event", "helpscout_open", {
    event_category: "support_engagement",
    event_label: "helpscout_beacon_opened",
    custom_parameters: {
      support_channel: "helpscout_beacon",
      timestamp: new Date().toISOString(),
    },
  });
};

export const trackHelpScoutMessageSent = () => {
  sendGAEvent("event", "helpscout_message", {
    event_category: "support_engagement",
    event_label: "helpscout_message_sent",
    custom_parameters: {
      support_channel: "helpscout_beacon",
      action_type: "message_sent",
      timestamp: new Date().toISOString(),
    },
  });
};

// Feature Discovery Events
export const trackFeatureDiscovery = (
  featureName: string,
  discoveryMethod: string
) => {
  sendGAEvent("event", "feature_discovery", {
    event_category: "feature_engagement",
    event_label: `feature_discovered_${featureName}`,
    custom_parameters: {
      feature_name: featureName,
      discovery_method: discoveryMethod, // 'scroll', 'click', 'navigation'
      timestamp: new Date().toISOString(),
    },
  });
};

// Form Interaction Events
export const trackFormStart = (formName: string) => {
  sendGAEvent("event", "form_start", {
    event_category: "form_engagement",
    event_label: `form_started_${formName}`,
    custom_parameters: {
      form_name: formName,
      interaction_type: "form_start",
      timestamp: new Date().toISOString(),
    },
  });
};

export const trackFormFieldInteraction = (
  formName: string,
  fieldName: string
) => {
  smartSendGAEvent("form_field_interaction", {
    event_category: "form_engagement",
    event_label: `field_interacted_${formName}_${fieldName}`,
    custom_parameters: {
      form_name: formName,
      field_name: fieldName,
      interaction_type: "field_focus",
      timestamp: new Date().toISOString(),
    },
  });
};

export const trackFormSubmission = (formName: string, success: boolean) => {
  sendGAEvent("event", "form_submit", {
    event_category: "form_engagement",
    event_label: `form_submitted_${formName}`,
    custom_parameters: {
      form_name: formName,
      submission_success: success,
      interaction_type: "form_submit",
      timestamp: new Date().toISOString(),
    },
  });
};

export const trackFormAbandon = (formName: string, lastField: string) => {
  sendGAEvent("event", "form_abandon", {
    event_category: "form_engagement",
    event_label: `form_abandoned_${formName}`,
    custom_parameters: {
      form_name: formName,
      last_field: lastField,
      interaction_type: "form_abandon",
      timestamp: new Date().toISOString(),
    },
  });
};

// Error Tracking Events
export const trackJavaScriptError = (error: Error, errorInfo?: string) => {
  sendGAEvent("event", "javascript_error", {
    event_category: "error_tracking",
    event_label: `js_error_${error.name}`,
    custom_parameters: {
      error_name: error.name,
      error_message: error.message,
      error_stack: error.stack?.substring(0, 500), // Limit stack trace length
      error_info: errorInfo,
      timestamp: new Date().toISOString(),
    },
  });
};

export const trackNetworkError = (
  url: string,
  status: number,
  method: string
) => {
  sendGAEvent("event", "network_error", {
    event_category: "error_tracking",
    event_label: `network_error_${status}`,
    custom_parameters: {
      request_url: url,
      status_code: status,
      http_method: method,
      timestamp: new Date().toISOString(),
    },
  });
};

// External Link Tracking
export const trackExternalLinkClick = (
  url: string,
  linkText: string,
  location: string
) => {
  sendGAEvent("event", "external_link_click", {
    event_category: "navigation",
    event_label: `external_link_clicked`,
    custom_parameters: {
      destination_url: url,
      link_text: linkText,
      click_location: location,
      timestamp: new Date().toISOString(),
    },
  });
};

// User Flow Tracking
export const trackUserFlowStep = (
  flowName: string,
  stepName: string,
  stepNumber: number
) => {
  sendGAEvent("event", "user_flow_step", {
    event_category: "user_journey",
    event_label: `flow_step_${flowName}_${stepName}`,
    custom_parameters: {
      flow_name: flowName,
      step_name: stepName,
      step_number: stepNumber,
      timestamp: new Date().toISOString(),
    },
  });
};

// User Tracking (Privacy Compliant)
export const setUserProperties = (properties: {
  userType?: "new" | "returning" | "trial" | "premium";
  registrationDate?: string;
  onboardingCompleted?: boolean;
  userSegment?: string;
}) => {
  // Set GA4 user properties (non-PII only)
  if (typeof window !== "undefined" && window.gtag) {
    window.gtag("config", "G-X2TRPZWBFD", {
      user_properties: {
        user_type: properties.userType,
        registration_month: properties.registrationDate
          ? new Date(properties.registrationDate).toISOString().substring(0, 7)
          : undefined, // Only year-month
        onboarding_completed: properties.onboardingCompleted,
        user_segment: properties.userSegment,
      },
    });
  }

  // Log for development
  if (process.env.NODE_ENV === "development") {
    console.log("👤 User Properties Set:", properties);
  }
};

export const setUserId = (hashedUserId: string) => {
  // Use hashed/anonymized user ID - never raw email or PII
  if (typeof window !== "undefined" && window.gtag) {
    window.gtag("config", "G-X2TRPZWBFD", {
      user_id: hashedUserId,
    });
  }

  // Also store locally for session tracking
  localStorage.setItem("analytics_user_id", hashedUserId);

  // Log for development
  if (process.env.NODE_ENV === "development") {
    console.log("🆔 User ID Set:", hashedUserId);
  }
};

export const getUserId = (): string | null => {
  if (typeof window === "undefined") return null;
  return localStorage.getItem("analytics_user_id");
};

// Generate privacy-compliant user identifier
export const generateAnonymousUserId = (email?: string): string => {
  if (email) {
    // Create a consistent hash of the email (not reversible)
    return btoa(email)
      .replace(/[^a-zA-Z0-9]/g, "")
      .substring(0, 16);
  }

  // Fallback to random ID if no email
  const randomId =
    Math.random().toString(36).substring(2, 15) +
    Math.random().toString(36).substring(2, 15);
  return randomId;
};

// Session Analytics
export const trackSessionStart = () => {
  const sessionId = Math.random().toString(36).substring(2, 15);
  const userId = getUserId();

  sessionStorage.setItem("analytics_session_id", sessionId);
  sessionStorage.setItem("session_start_time", Date.now().toString());

  smartSendGAEvent("session_start", {
    event_category: "session_analytics",
    event_label: "session_started",
    custom_parameters: {
      session_id: sessionId,
      has_user_id: !!userId,
      timestamp: new Date().toISOString(),
    },
  });
};

export const trackSessionEnd = () => {
  const sessionId = sessionStorage.getItem("analytics_session_id");
  const startTime = sessionStorage.getItem("session_start_time");

  if (sessionId && startTime) {
    const sessionDuration = Date.now() - parseInt(startTime);

    smartSendGAEvent("session_end", {
      event_category: "session_analytics",
      event_label: "session_ended",
      custom_parameters: {
        session_id: sessionId,
        session_duration_ms: sessionDuration,
        timestamp: new Date().toISOString(),
      },
    });
  }
};

// Performance Tracking
export const trackPerformanceMetric = (
  metricName: string,
  value: number,
  unit: string = "ms"
) => {
  smartSendGAEvent("performance_metric", {
    event_category: "performance",
    event_label: `performance_${metricName}`,
    custom_parameters: {
      metric_name: metricName,
      metric_value: value,
      metric_unit: unit,
      timestamp: new Date().toISOString(),
    },
  });
};

// Generic event tracker for custom use cases
export const trackCustomEvent = (
  eventName: string,
  category: string,
  label: string,
  customParams: Record<string, any> = {}
) => {
  smartSendGAEvent(eventName, {
    event_category: category,
    event_label: label,
    custom_parameters: {
      timestamp: new Date().toISOString(),
      ...customParams,
    },
  });
};
